/**
 * LinkedIn share-profile QR **shape validation** — U5 of
 * plans/2026-08-25-001-lead-scanner-implementation-plan.md (Key Technical
 * Decision 4).
 *
 * Deliberately a zero-import leaf. The actual zxing-wasm decoder lives in
 * `qr-decode.ts`, which imports the WASM binary through a Vite `?url` asset
 * import — a module specifier only `tsconfig.miniapps.json` (with
 * `types: ["vite/client"]` and Bundler resolution) can resolve. The test
 * program (`tsconfig.tests.json`, which extends the CommonJS root config)
 * cannot, so the shape check and the decoder's function type live here where
 * both programs and `bun test` can reach them. Same reason
 * `_shared/fetch-error-types.ts` is split out from `_shared/fetch-error.ts`.
 *
 * The regex is the harness's, verbatim
 * (`explorations/lead-scanner-extraction/decode-qr.ts:29`) — the exploration
 * stays untouched as historical record, this is the single app-code
 * declaration.
 */

export const LINKEDIN_PROFILE_URL_PATTERN = /^https?:\/\/(www\.)?linkedin\.com\/in\/[^/?]+/;

/**
 * The canonical profile URL a decoded QR payload points at, or `null` if the
 * payload isn't a LinkedIn profile URL at all (a conference
 * registration-system code, a vCard, a wifi string, an event agenda link —
 * all real things badges carry).
 *
 * Returns the MATCH, not the whole payload: LinkedIn's share QR appends
 * `?fromQR=1` and often a trailing slash, neither of which belongs in a
 * `linkedin` field that ends up on a Salesforce record. The pattern's
 * `[^/?]+` already stops at the slug boundary, so the match IS the canonical
 * `https://www.linkedin.com/in/<slug>` form.
 */
export function linkedInProfileUrlFrom(payload: string): string | null {
  return LINKEDIN_PROFILE_URL_PATTERN.exec(payload.trim())?.[0] ?? null;
}

/** Predicate form of {@link linkedInProfileUrlFrom} — the prototype DEV
 *  NOTE's non-optional validation step ("conference badges routinely carry
 *  the event's own registration-system QR, which must not be mistaken for a
 *  LinkedIn identity", docs/lead-scanner-prototype.html ~line 830). */
export function isLinkedInProfileUrl(payload: string): boolean {
  return linkedInProfileUrlFrom(payload) !== null;
}

/**
 * A looser, case-insensitive, optional-scheme variant of
 * {@link LINKEDIN_PROFILE_URL_PATTERN}, used only by
 * {@link normalizeLinkedInInput} below to recognize the shapes reps
 * actually paste — `linkedin.com/in/x`, `www.linkedin.com/in/x`,
 * `HTTPS://WWW.LINKEDIN.COM/in/x` — none of which the harness's
 * byte-for-byte pattern is meant to match. Kept separate rather than
 * loosening the shared constant, since that one is also the QR-decode
 * routing check (U5's registration-code rejection depends on it staying
 * exactly as-is).
 */
const LINKEDIN_URL_LOOSE_PATTERN = /^(?:https?:\/\/)?(?:www\.)?linkedin\.com\/in\/([^/?]+)/i;

/** A domain-shaped string (contains `.com`) is never treated as a bare
 *  slug — that's the difference between "the rep pasted their own slug"
 *  and "the rep pasted some other company's URL and we should leave it
 *  alone" (round-2 plan U6). */
const LOOKS_LIKE_A_DOMAIN_PATTERN = /\.com/i;

/** A bare slug: letters/digits/underscore/hyphen/dot/percent only, with an
 *  optional leading LinkedIn `in/` path segment stripped first (a rep who
 *  typed `in/jane` typed the URL's own path segment without the rest of
 *  the URL). */
const BARE_SLUG_PATTERN = /^[\w\-.%]+$/;

/**
 * Canonicalizes whatever a rep typed, pasted, or scanned for the LinkedIn
 * field into `https://www.linkedin.com/in/<slug>` — the one shape every
 * downstream consumer (the SFDC submit payload, the review-form display)
 * can rely on.
 *
 * R14, U6 of plans/2026-08-25-002-lead-scanner-round2-plan.md. Order of
 * attempts, each one falling through to the next:
 *
 * 1. Empty/whitespace-only → `''`.
 * 2. Already-canonical (or tracking-decorated / trailing-slash-decorated)
 *    URL → {@link linkedInProfileUrlFrom}'s match IS the canonical form,
 *    since its `[^/?]+` construction already stops at the slug boundary.
 * 3. Scheme-less or mixed-case host (`linkedin.com/in/x`,
 *    `WWW.LINKEDIN.COM/in/X`) → {@link LINKEDIN_URL_LOOSE_PATTERN}, rebuilt
 *    onto the canonical lowercase `https://www.linkedin.com` prefix (the
 *    slug's own case is preserved exactly as typed).
 * 4. A bare slug (`jane-doe-123`, or `in/jane` — the path segment typed
 *    without the rest of the URL) that isn't itself domain-shaped.
 * 5. Anything else (a non-LinkedIn URL, a bare non-LinkedIn domain, free
 *    text) is returned UNCHANGED — never destroy something we can't parse;
 *    the rep sees exactly what they typed.
 */
export function normalizeLinkedInInput(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed) return '';

  const direct = linkedInProfileUrlFrom(trimmed);
  if (direct) return direct;

  const looseMatch = LINKEDIN_URL_LOOSE_PATTERN.exec(trimmed);
  if (looseMatch) return `https://www.linkedin.com/in/${looseMatch[1]}`;

  const slugCandidate = trimmed.replace(/^in\//i, '');
  if (!LOOKS_LIKE_A_DOMAIN_PATTERN.test(slugCandidate) && BARE_SLUG_PATTERN.test(slugCandidate)) {
    return `https://www.linkedin.com/in/${slugCandidate}`;
  }

  return raw;
}

/**
 * The photo-intake pipeline's view of a QR decoder: original file bytes in,
 * the **raw decoded payload** (or `null` when the photo carries no readable
 * QR at all) out.
 *
 * Deliberately raw, not pre-validated: the decoder's job is "read whatever
 * QR is in these bytes", and deciding what a payload MEANS is the pipeline's
 * (`photo-intake.ts` runs `linkedInProfileUrlFrom` on it). That keeps the
 * shape policy in one place, and keeps the registration-code rejection
 * observable end-to-end in the pipeline's own tests rather than hidden
 * inside the WASM adapter.
 *
 * Declared here (not in `qr-decode.ts`) so `photo-intake.ts` can be typed
 * against it without pulling the WASM module into every program that reads
 * the pipeline.
 */
export type QrDecoder = (file: Blob) => Promise<string | null>;
